/**
 * Momentum Detail Page - per-entity momentum with KPIs and 30-day timeline chart.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  Eye,
  Users,
  DollarSign,
  Flame,
  Target,
  TrendingUp,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import momentumService from '../services/momentumService';
import {
  DealMomentum,
  MomentumTimelineResponse,
} from '../types/dealMomentum';
import MomentumBadge from '../components/MomentumBadge';
import VelocityIndicator from '../components/VelocityIndicator';

function formatCurrency(n?: number | null): string {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDate(s?: string | null): string {
  return s ? new Date(s).toLocaleString() : '—';
}

const MomentumDetailPage: React.FC = () => {
  const { entityKind, entityId } = useParams<{
    entityKind: string;
    entityId: string;
  }>();
  const kind = (entityKind === 'startup' ? 'startup' : 'project') as
    | 'project'
    | 'startup';
  const eid = Number(entityId);

  const [momentum, setMomentum] = useState<DealMomentum | null>(null);
  const [timeline, setTimeline] = useState<MomentumTimelineResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!eid) {
      toast.error('Invalid entity');
      setLoading(false);
      return;
    }
    setLoading(true);
    const params =
      kind === 'project' ? { project_id: eid } : { startup_id: eid };
    const [sigRes, tlRes] = await Promise.all([
      momentumService.fetchSignals(params),
      momentumService.fetchTimeline({ ...params, days: 30 }),
    ]);
    if (sigRes.error) {
      toast.error(sigRes.error);
    } else {
      const list = sigRes.data || [];
      setMomentum(list[0] || null);
    }
    if (tlRes.error) {
      toast.error(tlRes.error);
    } else {
      setTimeline(tlRes.data || null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, eid]);

  const handleRefresh = async () => {
    setRefreshing(true);
    const res = await momentumService.refresh({
      entity_kind: kind,
      entity_id: eid,
    });
    setRefreshing(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setMomentum(res.data);
      toast.success('Momentum refreshed');
      // Reload timeline
      const tlRes = await momentumService.fetchTimeline(
        kind === 'project' ? { project_id: eid } : { startup_id: eid }
      );
      if (tlRes.data) setTimeline(tlRes.data);
    }
  };

  // Timeline chart dims
  const chartWidth = 760;
  const chartHeight = 220;
  const padding = { top: 15, right: 15, bottom: 30, left: 40 };

  const chart = useMemo(() => {
    const pts = timeline?.points || [];
    if (pts.length === 0) return null;
    const innerW = chartWidth - padding.left - padding.right;
    const innerH = chartHeight - padding.top - padding.bottom;
    const maxV = Math.max(1, ...pts.map((p) => p.velocity_score));
    const minV = Math.min(0, ...pts.map((p) => p.velocity_score));
    const range = maxV - minV || 1;
    const points = pts.map((p, i) => {
      const x =
        pts.length === 1
          ? padding.left + innerW / 2
          : padding.left + (i / (pts.length - 1)) * innerW;
      const y =
        padding.top + innerH - ((p.velocity_score - minV) / range) * innerH;
      return { x, y, p };
    });
    const pathD = points
      .map((pt, i) => (i === 0 ? `M ${pt.x} ${pt.y}` : `L ${pt.x} ${pt.y}`))
      .join(' ');
    const areaD =
      pathD +
      ` L ${points[points.length - 1].x} ${padding.top + innerH}` +
      ` L ${points[0].x} ${padding.top + innerH} Z`;
    return { points, pathD, areaD, maxV, minV };
  }, [timeline]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading...
        </div>
      </DashboardLayout>
    );
  }

  if (!momentum) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <p className="text-sm text-gray-600 mb-4">
            No momentum data for this entity yet.
          </p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Compute now
          </button>
          <div className="mt-4">
            <Link
              to="/momentum"
              className="text-blue-600 hover:underline text-sm"
            >
              Back to momentum dashboard
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const entityName =
    momentum.project?.project_name ||
    momentum.startup?.name ||
    (kind === 'project' ? `Project #${eid}` : `Startup #${eid}`);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/momentum"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to momentum
        </Link>

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <Flame className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {entityName}
                </h1>
                <p className="text-sm text-gray-600 mt-0.5 capitalize">
                  {kind} #{eid}
                  {momentum.last_computed_at &&
                    ` · last computed ${formatDate(momentum.last_computed_at)}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MomentumBadge tier={momentum.momentum_tier} />
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                )}
                Refresh Now
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col items-start">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Velocity score
              </div>
              <VelocityIndicator velocity={momentum.velocity_score} size="lg" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Round progress
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-3 bg-gradient-to-r from-blue-400 to-blue-600"
                    style={{
                      width: `${Math.max(
                        0,
                        Math.min(100, momentum.round_progress_percent)
                      )}%`,
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {Math.round(momentum.round_progress_percent)}%
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                Deal room engagement
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {Math.round(momentum.deal_room_engagement_score)}
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={<Eye className="w-4 h-4 text-blue-500" />}
            label="Unique views"
            value={momentum.unique_investor_views}
          />
          <MetricCard
            icon={<TrendingUp className="w-4 h-4 text-green-500" />}
            label="Views this week"
            value={momentum.views_this_week}
            delta={momentum.views_this_week - momentum.views_last_week}
          />
          <MetricCard
            icon={<Users className="w-4 h-4 text-purple-500" />}
            label="Match requests"
            value={momentum.match_requests}
          />
          <MetricCard
            icon={<DollarSign className="w-4 h-4 text-amber-500" />}
            label="Soft commits"
            value={`${formatCurrency(
              momentum.soft_commitment_total
            )} (${momentum.soft_commitment_count})`}
          />
        </div>

        {/* Timeline chart */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center">
              <Target className="w-4 h-4 mr-1.5 text-blue-500" />
              30-day momentum timeline
            </h3>
            {timeline && (
              <span className="text-xs text-gray-500">
                {timeline.points.length} data point
                {timeline.points.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {!chart ? (
            <p className="text-sm text-gray-500">
              No timeline data available yet.
            </p>
          ) : (
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              width="100%"
              height={chartHeight}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Grid */}
              {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
                const y =
                  padding.top +
                  (chartHeight - padding.top - padding.bottom) * (1 - r);
                const v = (chart.minV + r * (chart.maxV - chart.minV)).toFixed(
                  0
                );
                return (
                  <g key={i}>
                    <line
                      x1={padding.left}
                      y1={y}
                      x2={chartWidth - padding.right}
                      y2={y}
                      stroke="#E5E7EB"
                      strokeWidth={1}
                    />
                    <text
                      x={padding.left - 6}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      className="fill-gray-500"
                      style={{ fontSize: 10 }}
                    >
                      {v}
                    </text>
                  </g>
                );
              })}
              <path d={chart.areaD} fill="rgba(239, 68, 68, 0.15)" />
              <path
                d={chart.pathD}
                fill="none"
                stroke="#DC2626"
                strokeWidth={2}
              />
              {chart.points.map((pt, i) => (
                <g key={i}>
                  <circle cx={pt.x} cy={pt.y} r={3} fill="#DC2626" />
                  <title>
                    {pt.p.date} — velocity {pt.p.velocity_score.toFixed(1)}
                  </title>
                </g>
              ))}
              {/* Start/end date labels */}
              {chart.points.length > 0 && (
                <>
                  <text
                    x={chart.points[0].x}
                    y={chartHeight - 8}
                    textAnchor="start"
                    className="fill-gray-500"
                    style={{ fontSize: 10 }}
                  >
                    {chart.points[0].p.date}
                  </text>
                  <text
                    x={chart.points[chart.points.length - 1].x}
                    y={chartHeight - 8}
                    textAnchor="end"
                    className="fill-gray-500"
                    style={{ fontSize: 10 }}
                  >
                    {chart.points[chart.points.length - 1].p.date}
                  </text>
                </>
              )}
            </svg>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  delta?: number;
}> = ({ icon, label, value, delta }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>
      {icon}
    </div>
    <div className="text-xl font-bold text-gray-900 mt-1">{value}</div>
    {delta !== undefined && delta !== 0 && (
      <div
        className={`text-xs font-medium mt-0.5 ${
          delta > 0 ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {delta > 0 ? '+' : ''}
        {delta} vs last week
      </div>
    )}
  </div>
);

export default MomentumDetailPage;
